import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import { gear } from '@/assets/icons';
import {
	ActionSheet,
	ActionSheetAction,
} from '@/components/shared/ActionSheet';
import {
	AsaphDialog,
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import { PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import {
	Permissions,
	useCreateSetlistMutation,
	useDeleteSetlistMutation,
	useUpdateSetlistMutation,
} from '@/graphql';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAuth } from '@/providers/AuthProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { SetlistsContext, useSetlists } from '@/providers/SetlistsProvider';
import { Fade, Slide, SvgIcon } from '@material-ui/core';
import { format, formatISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { pathOr } from 'ramda';

import { ILayoutProps } from '../../Layout';
import { CreateSetlistRoot } from './components/CreateSetlistRoot';
import { SetlistSettings } from './components/SetlistSettings/SetlistSettings';
import styles from './CreateSetlistPanel.module.scss';

export enum ECreateSetlistDisplayState {
	ROOT,
	SETLIST_SETTINGS,
}

export interface IPropsCreateSetlistDisplay {
	isViewOnly: boolean;
	config: CreateSetlistPanelProps['config'];
	handleClose: CreateSetlistPanelProps['callbackFn'];
	selectedSongIds: string[];
	setSelectedSongIds: React.Dispatch<React.SetStateAction<string[]>>;
	setStartTime: React.Dispatch<React.SetStateAction<string>>;
	setStartDate: React.Dispatch<React.SetStateAction<string>>;
	setSelectedServiceId: React.Dispatch<
		React.SetStateAction<string | undefined | null>
	>;
	startTime: string;
	startDate: string;
	selectedServiceId?: string | null;
	draftSetlist?: SetlistsContext['draftSetlists'][0];
	setlistData?: SetlistsContext['setlists'][0];
	openSonglistWithCallback: CreateSetlistPanelProps['openSonglistWithCallback'];
	openSong: CreateSetlistPanelProps['openSong'];
	// pushDisplayState: (newState: ECreateSetlistDisplayState) => void;
	popDisplayState: () => void;
	saveDraftSetlist: () => Promise<void>;
	setAccountOpen: ILayoutProps['setAccountOpen'];
}

const displayStateComponents: Map<
	ECreateSetlistDisplayState,
	{
		title?: string | string[];
		component: React.FC<IPropsCreateSetlistDisplay>;
	}
> = new Map([
	[
		ECreateSetlistDisplayState.ROOT,
		{
			component: CreateSetlistRoot,
			title: 'Create setlist',
		},
	],
	[
		ECreateSetlistDisplayState.SETLIST_SETTINGS,
		{
			component: SetlistSettings,
			title: 'Create setlist',
		},
	],
]);
const displayStateKeys = Array.from(displayStateComponents.keys());
const defaultDisplayStateStack = [ECreateSetlistDisplayState.ROOT];

export interface CreateSetlistPanelProps {
	setOpen: (open: boolean) => void;
	callbackFn: (res: {
		setlistId?: string;
		songsLength?: number;
		removed?: boolean;
		savedDraft?: boolean;
	}) => void;
	config: {
		setlistId?: string;
		selectedSongIds?: string[];
		viewOnly?: boolean;
	};
	openSonglistWithCallback: ILayoutProps['openSonglistWithCallback'];
	openSong: ILayoutProps['openSong'];
	setAccountOpen: ILayoutProps['setAccountOpen'];
}

export const CreateSetlistPanel: React.FC<CreateSetlistPanelProps> = ({
	setOpen,
	callbackFn,
	config,
	openSonglistWithCallback,
	openSong,
	setAccountOpen,
}) => {
	const { user } = useAuth();
	const { hasPermission } = usePermissions();
	const { setlists, draftSetlists, refetch } = useSetlists();
	const { handleError } = useErrorHandler();
	const { enqueueSnackbar } = useSnackbar();

	const [startTime, setStartTime] = useState('');
	const [startDate, setStartDate] = useState('');
	const [selectedSongIds, setSelectedSongIds] = useState<string[]>(
		config.selectedSongIds || [],
	);
	const [selectedServiceId, setSelectedServiceId] = useState<
		string | null | undefined
	>(); // String represents a selected service, null represents custom service
	// and undefined represents an empty state
	const [deleteActionOpen, setDeleteActionOpen] = useState(false);
	const [confirmCloseActionOpen, setConfirmCloseActionOpen] = useState(false);

	const draftSetlist = draftSetlists.find((ds) => ds.id === config.setlistId);
	const setlist = config.setlistId
		? setlists.find((s) => s.id === config.setlistId)
		: undefined;

	useEffect(() => {
		if (draftSetlist) {
			setStartTime(format(new Date(draftSetlist.startAt), 'HH:mm'));
			setStartDate(draftSetlist.startAt);
			setSelectedSongIds(draftSetlist.teamSongIds);
			setSelectedServiceId(draftSetlist.serviceId || undefined);
		}
	}, [draftSetlist]);

	const isViewOnly = config.viewOnly ?? true;

	const { mutateAsync: deleteAsync } = useDeleteSetlistMutation();
	const { mutateAsync: createAsync } = useCreateSetlistMutation();
	const { mutateAsync: updateAsync } = useUpdateSetlistMutation();

	const [displayStateStack, setDisplayStateStack] = useState(
		defaultDisplayStateStack,
	);
	const [displayState, setDisplayState] =
		useState<ECreateSetlistDisplayState>(
			getDisplayState(displayStateStack),
		);

	const title = getNameForDisplayState(displayState);

	useEffect(() => {
		setDisplayState(getDisplayState(displayStateStack));
	}, [displayStateStack]);

	const popDisplayState = useCallback(() => {
		if (displayStateStack.length > 1) {
			const newStack = [...displayStateStack];
			newStack.pop();
			setDisplayStateStack(newStack);
		}
	}, [displayStateStack]);

	const pushDisplayState = useCallback(
		(newState: ECreateSetlistDisplayState) => {
			setDisplayStateStack([...displayStateStack, newState]);
		},
		[displayStateStack],
	);

	const handleSongSettingsClick = useCallback(() => {
		pushDisplayState(ECreateSetlistDisplayState.SETLIST_SETTINGS);
	}, [pushDisplayState]);

	const deleteDraftSetlist = async () => {
		if (draftSetlist) {
			try {
				await deleteAsync({
					setlistId: draftSetlist.id,
				});
				refetch();
				enqueueSnackbar('Draft setlist deleted');
				handleClose({});
			} catch (error) {
				handleError({
					errorCode: 'SetlistDraftError',
					error,
					friendlyMessage: `There was an issue with deleting your draft`,
				});
			}
		}
	};

	const saveDraftSetlist = async () => {
		try {
			const input = {
				startAt: getZonedStartDateTimeISO(startTime, startDate),
				teamSongIds: selectedSongIds,
				serviceId: selectedServiceId,
				draft: true,
				generateSettings: {
					totalSongPositions: 4,
					postSermonSongPosition: 3,
				},
			};
			if (draftSetlist) {
				await updateAsync({
					setlistId: draftSetlist.id,
					input,
				});
			} else {
				await createAsync({
					input,
				});
			}
			enqueueSnackbar('Draft setlist saved');
			// If we are saving a draft, then we should notify the original
			// called of the panel about it
			handleClose({
				savedDraft: true,
			});
			refetch();
		} catch (error) {
			handleError({
				errorCode: 'SetlistDraftError',
				error,
				friendlyMessage: `There was an issue with saving your draft`,
			});
		}
	};

	const handleClose = useCallback<CreateSetlistPanelProps['callbackFn']>(
		({ setlistId, songsLength, removed, savedDraft }) => {
			setOpen(false);
			callbackFn({ setlistId, songsLength, removed, savedDraft });
			setDisplayStateStack(defaultDisplayStateStack);
		},
		[callbackFn, setOpen],
	);

	// const checkForChangesBeforeClose = () => {
	// 	if (isViewOnly) {
	// 		return handleClose({});
	// 	}
	// 	setConfirmCloseActionOpen(true);
	// };

	return (
		<AsaphDialog
			open={true}
			onClose={() => handleClose({})}
			paperClassName={styles['panel']}
		>
			<div className={styles['toolbar']}>
				<AsaphDialogHeader
					leadingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: renderHeaderStart(),
					}}
					title={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						label: title,
					}}
					trailingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: renderHeaderEnd(),
					}}
				/>
			</div>
			{displayState !== ECreateSetlistDisplayState.ROOT && (
				<div className={styles['toolbar-divider']}>
					<hr />
				</div>
			)}
			<section className={styles['content']}>
				{displayStateKeys.map((key) => {
					const displayStateComponent =
						displayStateComponents.get(key);
					if (!displayStateComponent) {
						return null;
					}
					const Component = displayStateComponent.component;
					if (key === ECreateSetlistDisplayState.ROOT) {
						return (
							<Fade
								key={key}
								in={displayState === key}
								timeout={PANEL_TRANSITION_TIMEOUT}
								mountOnEnter
								unmountOnExit
							>
								<div className={styles['content-container']}>
									<Component
										isViewOnly={isViewOnly}
										config={config}
										handleClose={handleClose}
										selectedSongIds={selectedSongIds}
										setSelectedSongIds={setSelectedSongIds}
										startTime={startTime}
										startDate={startDate}
										draftSetlist={draftSetlist}
										setlistData={setlist}
										selectedServiceId={selectedServiceId}
										openSonglistWithCallback={
											openSonglistWithCallback
										}
										openSong={openSong}
										setStartDate={setStartDate}
										setStartTime={setStartTime}
										setSelectedServiceId={
											setSelectedServiceId
										}
										popDisplayState={popDisplayState}
										saveDraftSetlist={saveDraftSetlist}
										setAccountOpen={setAccountOpen}
									/>
								</div>
							</Fade>
						);
					}

					return (
						<Slide
							key={key}
							direction="left"
							in={
								displayState === key ||
								displayStateStack.indexOf(key) >= 0
							}
							timeout={PANEL_TRANSITION_TIMEOUT}
							mountOnEnter
							unmountOnExit
						>
							<div
								className={styles['content-container']}
								// Needed to prevent underlying Components' absolute positioned elements be on top of new Component
								style={{
									zIndex: displayStateStack.length,
								}}
							>
								<Component
									isViewOnly={isViewOnly}
									config={config}
									handleClose={handleClose}
									selectedSongIds={selectedSongIds}
									setSelectedSongIds={setSelectedSongIds}
									startTime={startTime}
									startDate={startDate}
									draftSetlist={draftSetlist}
									setlistData={setlist}
									selectedServiceId={selectedServiceId}
									openSonglistWithCallback={
										openSonglistWithCallback
									}
									openSong={openSong}
									setStartDate={setStartDate}
									setStartTime={setStartTime}
									setSelectedServiceId={setSelectedServiceId}
									popDisplayState={popDisplayState}
									saveDraftSetlist={saveDraftSetlist}
									setAccountOpen={setAccountOpen}
								/>
							</div>
						</Slide>
					);
				})}

				<ActionSheet
					open={deleteActionOpen}
					title={
						<span>
							Are you sure you want to delete this draft setlist?
						</span>
					}
					onClose={() => setDeleteActionOpen(false)}
				>
					<ActionSheetAction
						label={'Remove for everyone'}
						type="danger"
						onActionClick={deleteDraftSetlist}
					/>
				</ActionSheet>

				{/* <ActionSheet
					open={confirmCloseActionOpen}
					title={
						<span>
							You have unsaved changes, do you want to save a
							draft?
						</span>
					}
					onClose={() => setConfirmCloseActionOpen(false)}
				>
					<ActionSheetAction
						label={'Yes, Save draft setlist'}
						onActionClick={deleteDraftSetlist}
					/>
					<ActionSheetAction
						label={'No, close dialog'}
						onActionClick={() => handleClose({})}
					/>
				</ActionSheet> */}
			</section>
		</AsaphDialog>
	);

	function renderHeaderStart(): ReactNode {
		switch (displayState) {
			case ECreateSetlistDisplayState.ROOT:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Cancel"
						handlePress={() => handleClose({})}
					/>
				);
			default:
				return (
					<AsaphDialogHeaderButton
						type="icon"
						label="Back"
						handlePress={popDisplayState}
					/>
				);
		}
	}

	function renderHeaderEnd(): ReactNode {
		switch (displayState) {
			case ECreateSetlistDisplayState.ROOT:
				if (!!config.setlistId) {
					return hasPermission(Permissions.SetlistDelete) ? (
						<AsaphDialogHeaderButton
							type="icon"
							label="Settings"
							handlePress={handleSongSettingsClick}
						>
							<SvgIcon component={gear} />
						</AsaphDialogHeaderButton>
					) : null;
				}
				return draftSetlist &&
					hasPermission(Permissions.SetlistDelete) ? (
					<AsaphDialogHeaderButton
						type="text"
						label="Delete"
						textClassName={styles['delete-label']}
						handlePress={() => setDeleteActionOpen(true)}
					/>
				) : null;
			default:
				return null;
		}
	}
};

function getDisplayState(displayStateStack: ECreateSetlistDisplayState[]) {
	return displayStateStack.length
		? displayStateStack[displayStateStack.length - 1]
		: ECreateSetlistDisplayState.ROOT;
}

function getNameForDisplayState(
	displayState: ECreateSetlistDisplayState,
): string {
	const displayStateComponent = displayStateComponents.get(displayState);
	if (displayStateComponent) {
		return pathOr('', ['title'], displayStateComponent);
	}
	return '';
}

export const getZonedStartDateTimeISO = (
	startTime?: string,
	startDate?: string,
) => {
	const now = new Date();
	const [hours, minutes] = (startTime || format(now, 'h:m')).split(':');
	const zonedStartDate = startDate ? new Date(startDate) : now;
	zonedStartDate.setHours(parseInt(hours));
	zonedStartDate.setMinutes(parseInt(minutes));
	return formatISO(zonedStartDate);
};
